<script setup lang="ts">
defineOptions({ inheritAttrs: false });

import { type Ref, type CSSProperties, ref, computed } from "vue";
import ListItem from "./components/ListItem.vue";

const props = withDefaults(
  defineProps<{
    data?: Record<string, any>[];
    columnNum?: number;
    gutter?: number;
    emptyText?: string;
  }>(),
  { data: () => [], gutter: 5 }
);

const itemStyle = computed(() => {
  const style: CSSProperties = {};
  if (props.columnNum && props.columnNum > 0) {
    style.flexBasis = `${100 / props.columnNum}%`;
  }

  style.padding = `0 ${props.gutter}px ${props.gutter}px 0`;
  return style;
});

const childrenStyles: Ref<Record<string, any>> = ref({});
const childrenProps: Ref<Record<string, any>> = ref({});

function setChildrenStyle(rowIndex: number, key: string, style: any) {
  childrenStyles.value = {
    ...childrenStyles.value,
    ...{ [`key${rowIndex}${key}`]: style },
  };
}

function setChildrenProps(rowIndex: number, key: string, props: any) {
  childrenProps.value = {
    ...childrenProps.value,
    ...{ [`key${rowIndex}${key}`]: props },
  };
}

function clearChildrenStyles() {
  childrenStyles.value = {};
}

function clearChildrenProps() {
  childrenProps.value = {};
}

const emit = defineEmits(["itemClick"]);
defineExpose({
  setChildrenStyle,
  setChildrenProps,
  clearChildrenStyles,
  clearChildrenProps,
});
</script>
<template>
  <div class="mp-list">
    <div
      class="mp-list__content"
      :style="{ padding: `${props.gutter}px 0 0 ${props.gutter}px` }"
      v-if="data.length > 0"
    >
      <div
        v-for="(item, index) in data"
        :key="index"
        :style="itemStyle"
        @click.stop="emit('itemClick', { item, index })"
      >
        <ListItem
          :item="item"
          :index="index"
          :styles="childrenStyles"
          :props="childrenProps"
        >
          <slot />
        </ListItem>
      </div>
    </div>
    <slot name="empty" v-else>
      <div class="mp-list__empty">
        <span>{{ emptyText }}</span>
      </div>
    </slot>
  </div>
</template>
<style lang="scss">
.mp-list {
  &,
  &__content {
    width: 100%;
  }

  &__content {
    display: inline-flex;
    flex-wrap: wrap;
  }

  &__empty {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #c0c4cc;
    font-size: 16px;
  }
}
</style>
